import { mockDB } from './mockDatabase';
import { User, PlanTier, PlanFeature, PlanConfig, PaymentConfig, Coupon, TaskAnalysis, ProjectAIAnalytics } from '../types';
import { config } from './config';
import { canUseAIFeatures, canUseAITaskAnalysis, canUseAISubtaskGeneration, logAIUsage, promptUpgradeIfNeeded } from './aiFeatureCheck';

const USE_MOCK = config.useMockData;
const API_BASE = config.apiBaseUrl;

// Helper function to get cookie value
const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
};

const fetchApi = async (endpoint: string, options: RequestInit = {}) => {
  const csrfToken = getCookie('csrf_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include' // Include cookies in requests
  });

  // Handle token refresh on 401
  if (response.status === 401) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.code === 'TOKEN_EXPIRED') {
      // Try to refresh token
      try {
        const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          credentials: 'include'
        });

        if (refreshResponse.ok) {
          // Retry the original request
          return fetchApi(endpoint, options);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
    }

    // Only auto-redirect to login for protected endpoints (not login endpoints)
    const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/admin-login') || endpoint.includes('/auth/social');
    if (!isAuthEndpoint) {
      localStorage.removeItem('pf_user');
      window.location.hash = '#/';
      throw new Error("Unauthorized");
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: any = new Error(errorData.message || `API Error: ${response.statusText}`);
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = errorData;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

export const getUser = async (): Promise<User | null> => {
  if (USE_MOCK) return mockDB.getUser();
  try {
    return await fetchApi('/auth/me');
  } catch {
    return null;
  }
};

export const logoutUser = async () => {
  try {
    await fetchApi('/auth/logout', { method: 'POST' });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('pf_user');
    window.location.hash = '#/';
  }
};

export const socialLogin = async (provider: string, email: string, name: string, avatar: string) => {
  if (USE_MOCK) {
    const res = await mockDB.socialLogin(provider, email, name, avatar);
    if (res.user) {
      await mockDB.saveUser(res.user);
    }
    return res;
  }
  const res = await fetchApi('/auth/social', {
    method: 'POST',
    body: JSON.stringify({ provider, email, name, avatar })
  });
  return res;
};

export const verifyMfa = async (userId: string, code: string, method?: 'totp' | 'email_otp') => {
  if (USE_MOCK) {
    const res = await mockDB.verifyMfa(userId, code);
    if (res.user) {
      await mockDB.saveUser(res.user);
    }
    return res;
  }
  return await fetchApi('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ userId, code, method })
  });
};

export const requestEmailMfa = async (userId: string) => {
  if (USE_MOCK) {
    return { sent: true };
  }
  return await fetchApi('/auth/mfa/email', {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
};

export const startMfaSetup = async () => {
  if (USE_MOCK) {
    return { secret: 'MOCKSECRET', otpauth: 'otpauth://totp/Kanbanboard:mock?secret=MOCKSECRET' };
  }
  return await fetchApi('/auth/mfa/setup', { method: 'POST' });
};

export const adminLogin = async (email?: string, password?: string) => {
  if (USE_MOCK) {
    // Create a proper admin user for admin portal login
    const adminUser: User = {
      id: 'admin_001',
      name: 'Admin User',
      email: 'info@kanbanboard.org',
      role: 'admin',
      plan: PlanTier.ENTERPRISE,
      avatarUrl: 'https://ui-avatars.com/api/?name=Admin+User&background=6366f1&color=fff',
      phoneNumber: '+919910754933',
      integrations: [],
      isMfaEnabled: false,
      isActive: true
    };
    await mockDB.saveUser(adminUser);
    return adminUser;
  }
  // Send admin credentials to backend - uses default admin if not provided
  const res = await fetchApi('/auth/admin-login', {
    method: 'POST',
    body: JSON.stringify({ email: email || 'info@kanbanboard.org', password })
  });
  // Return user (token is handled by cookies)
  return res.user;
};

export const updateUserProfile = async (u: User) => {
  if (USE_MOCK) return mockDB.saveUser(u);
  return await fetchApi('/users/me', { method: 'PUT', body: JSON.stringify(u) });
};

// Change user password
export const changeUserPassword = async (currentPassword: string, newPassword: string) => {
  if (USE_MOCK) {
    return { success: true, message: 'Password changed successfully' };
  }
  return await fetchApi('/users/me/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  });
};

// Change admin password
export const changeAdminPassword = async (currentPassword: string, newPassword: string) => {
  if (USE_MOCK) {
    return { success: true, message: 'Admin password changed successfully' };
  }
  return await fetchApi('/auth/change-admin-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  });
};

// Admin change user password
export const adminChangeUserPassword = async (userId: string, newPassword: string) => {
  if (USE_MOCK) {
    return { success: true, message: 'User password changed successfully' };
  }
  return await fetchApi(`/users/${userId}/change-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword })
  });
};

export const getAllUsers = async () => {
  if (USE_MOCK) return mockDB.getAllUsers();
  return await fetchApi('/users');
};

export const updateUserRoleAndPlan = async (userId: string, role: string, plan: PlanTier) => {
  if (USE_MOCK) return mockDB.updateUserRoleAndPlan(userId, role, plan);
  return await fetchApi(`/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role, plan })
  });
};

export const updateUserStatus = async (userId: string, isActive: boolean) => {
  if (USE_MOCK) return mockDB.updateUserStatus(userId, isActive);
  return await fetchApi(`/users/${userId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ isActive })
  });
};

export const deleteUser = async (userId: string) => {
  if (USE_MOCK) return mockDB.deleteUser(userId);
  return await fetchApi(`/users/${userId}`, { method: 'DELETE' });
};

export const requestEnterpriseUpgrade = async (user: User) => {
  if (USE_MOCK) return mockDB.requestEnterpriseUpgrade(user);
  return await fetchApi('/billing/enterprise-request', {
    method: 'POST',
    body: JSON.stringify({ user })
  });
};

// --- LOGS ---
export const getSystemLogs = async (userId?: string) => {
  if (USE_MOCK) return mockDB.getSystemLogs(userId);
  const query = userId ? `?userId=${userId}` : '';
  return await fetchApi(`/admin/logs${query}`);
};

export const deleteUserLogs = async (userId: string) => {
  if (USE_MOCK) return mockDB.deleteUserLogs(userId);
  return await fetchApi(`/admin/logs/user/${userId}`, { method: 'DELETE' });
};

// --- USER ACTIVITY LOGS ---
export const getUserActivityLogs = async () => {
  if (USE_MOCK) {
    // Mock: return from localStorage
    const raw = localStorage.getItem('pf_action_logs');
    return raw ? JSON.parse(raw) : [];
  }
  return await fetchApi('/user/activity-logs');
};

export const deleteActivityLogs = async (logIds: string[]) => {
  if (USE_MOCK) {
    // Mock: delete from localStorage
    const raw = localStorage.getItem('pf_action_logs');
    if (raw) {
      const logs = JSON.parse(raw);
      const filtered = logs.filter((log: any) => !logIds.includes(log.id));
      localStorage.setItem('pf_action_logs', JSON.stringify(filtered));
    }
    return { success: true, deleted: logIds.length };
  }
  return await fetchApi('/user/activity-logs', {
    method: 'DELETE',
    body: JSON.stringify({ logIds })
  });
};

export const clearOldActivityLogs = async (daysOld: number = 90) => {
  if (USE_MOCK) {
    // Mock: auto-clear from localStorage
    const raw = localStorage.getItem('pf_action_logs');
    if (raw) {
      const logs = JSON.parse(raw);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      const filtered = logs.filter((log: any) => new Date(log.date) > cutoffDate);
      localStorage.setItem('pf_action_logs', JSON.stringify(filtered));
      return { success: true, deleted: logs.length - filtered.length };
    }
    return { success: true, deleted: 0 };
  }
  return await fetchApi(`/user/activity-logs/clear-old?days=${daysOld}`, {
    method: 'DELETE'
  });
};

// --- PAYMENT CONFIG ---
export const getPaymentConfig = async () => {
  if (USE_MOCK) return mockDB.getPaymentConfig();
  return await fetchApi('/payment-config');
};

export const updatePaymentConfig = async (config: PaymentConfig) => {
  if (USE_MOCK) return mockDB.updatePaymentConfig(config);
  return await fetchApi('/admin/payment-config', {
    method: 'PUT',
    body: JSON.stringify(config)
  });
};

// --- COUPONS ---
export const getCoupons = async () => {
  if (USE_MOCK) return mockDB.getCoupons();
  return await fetchApi('/admin/coupons');
};

export const createCoupon = async (coupon: Coupon) => {
  if (USE_MOCK) return mockDB.createCoupon(coupon);
  return await fetchApi('/admin/coupons', {
    method: 'POST',
    body: JSON.stringify(coupon)
  });
};

export const deleteCoupon = async (id: string) => {
  if (USE_MOCK) return mockDB.deleteCoupon(id);
  return await fetchApi(`/admin/coupons/${id}`, { method: 'DELETE' });
};

// Get email restrictions for a coupon
export const getCouponRestrictions = async (couponId: string) => {
  return await fetchApi(`/admin/coupons/${couponId}/restrictions`);
};

// Add emails to coupon restrictions
export const addCouponRestrictions = async (couponId: string, emails: string[]) => {
  return await fetchApi(`/admin/coupons/${couponId}/restrictions`, {
    method: 'POST',
    body: JSON.stringify({ emails })
  });
};

// Remove email from coupon restrictions
export const removeCouponRestriction = async (couponId: string, email: string) => {
  return await fetchApi(`/admin/coupons/${couponId}/restrictions/${encodeURIComponent(email)}`, {
    method: 'DELETE'
  });
};

export const validateCoupon = async (code: string, planTier: PlanTier) => {
  if (USE_MOCK) return mockDB.validateCoupon(code, planTier);
  return await fetchApi('/billing/validate-coupon', {
    method: 'POST',
    body: JSON.stringify({ code, planTier })
  });
};

// --- OTHERS ---
export const getPlans = async () => {
  if (USE_MOCK) return mockDB.getPlans();
  return await fetchApi('/plans');
};

export const updatePlan = async (p: any) => {
  if (USE_MOCK) return mockDB.updatePlan(p);
  return await fetchApi(`/admin/plans/${p.tier}`, {
    method: 'PUT',
    body: JSON.stringify(p)
  });
};

export const getPlanFeatures = async () => {
  if (USE_MOCK) return mockDB.getPlanFeatures();
  return await fetchApi('/plan-features');
};

export const updatePlanFeature = async (f: any) => {
  if (USE_MOCK) return mockDB.updatePlanFeature(f);
  return await fetchApi(`/admin/plan-features/${f.id}`, {
    method: 'PUT',
    body: JSON.stringify(f)
  });
};

// Methodology Management APIs
export const getMethodologySettings = async () => {
  if (USE_MOCK) {
    return {
      id: '1',
      enableUserSelection: false,
      defaultMethodology: 'Kanban',
      availableMethodologies: ['Kanban'],
      updatedAt: new Date().toISOString()
    };
  }
  return await fetchApi('/admin/methodology-settings');
};

export const updateMethodologySettings = async (settings: any) => {
  if (USE_MOCK) {
    return settings;
  }
  return await fetchApi('/admin/methodology-settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  });
};

export const getAllMethodologies = async () => {
  if (USE_MOCK) {
    return [
      { id: '1', name: 'Kanban', description: 'Kanban methodology', isActive: true, isDefault: true, createdAt: new Date().toISOString() },
      { id: '2', name: 'Agile Scrum', description: 'Agile Scrum methodology', isActive: false, isDefault: false, createdAt: new Date().toISOString() },
      { id: '3', name: 'Waterfall', description: 'Waterfall methodology', isActive: false, isDefault: false, createdAt: new Date().toISOString() },
    ];
  }
  return await fetchApi('/admin/methodologies');
};

export const createMethodology = async (methodology: any) => {
  if (USE_MOCK) {
    return { id: Date.now().toString(), ...methodology, createdAt: new Date().toISOString() };
  }
  return await fetchApi('/admin/methodologies', {
    method: 'POST',
    body: JSON.stringify(methodology)
  });
};

export const updateMethodology = async (id: string, methodology: any) => {
  if (USE_MOCK) {
    return { id, ...methodology, updatedAt: new Date().toISOString() };
  }
  return await fetchApi(`/admin/methodologies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(methodology)
  });
};

export const deleteMethodology = async (id: string) => {
  if (USE_MOCK) {
    return { id };
  }
  return await fetchApi(`/admin/methodologies/${id}`, {
    method: 'DELETE'
  });
};

export const getAuthProviders = async (adminMode = false) => {
  if (USE_MOCK) return mockDB.getAuthProviders();
  return await fetchApi('/auth/providers');
};

export const updateAuthProvider = async (id: string, data: any) => {
  if (USE_MOCK) return mockDB.updateAuthProvider(id, data);
  return await fetchApi(`/auth/providers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const createAuthProvider = async (data: any) => {
  if (USE_MOCK) {
    // Mock implementation
    return data;
  }
  return await fetchApi('/auth/providers', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const deleteAuthProvider = async (id: string) => {
  if (USE_MOCK) {
    // Mock implementation
    return;
  }
  return await fetchApi(`/auth/providers/${id}`, {
    method: 'DELETE'
  });
};

export const checkUserRegistrationStatus = async (email: string) => {
  if (USE_MOCK) {
    // Mock: return registered for demo
    return 'registered';
  }
  
  try {
    const result = await fetchApi('/users/check-status', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    
    return result.exists ? 'registered' : 'not_registered';
  } catch (error) {
    console.error('[API] Error checking user status:', error);
    return 'error';
  }
};

export const sendTaskInvite = async (invitedEmail: string, projectId: string, taskId?: string, couponCode?: string) => {
  if (USE_MOCK) {
    console.log('[API] Mock: Sending invite to', invitedEmail);
    return { success: true, userExists: Math.random() > 0.5 };
  }

  const result = await fetchApi('/invites/send-task-invite', {
    method: 'POST',
    body: JSON.stringify({ 
      invitedEmail, 
      projectId, 
      taskId,
      couponCode 
    })
  });

  return result;
};

// Get all sent pending invites (for manage invites view)
export const getSentInvites = async (status?: string) => {
  if (USE_MOCK) {
    return mockDB.pendingInvites || [];
  }

  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }

  const response = await fetchApi(`/api/pending-invites?${params.toString()}`);
  return response;
};

export const getPendingInvites = async () => {
  if (USE_MOCK) {
    return [];
  }

  return await fetchApi('/invites/pending', {
    method: 'GET'
  });
};

export const acceptInvite = async (email: string, inviteToken: string, couponCode?: string) => {
  if (USE_MOCK) {
    return { success: true, projectId: '1' };
  }

  return await fetchApi('/invites/accept', {
    method: 'POST',
    body: JSON.stringify({ 
      email, 
      inviteToken,
      couponCode 
    })
  });
};
export const getProjects=async (userId: string)=> {
  console.log('[getProjects] Called with userId:', userId);
  console.log('[getProjects] USE_MOCK:', USE_MOCK);
  console.log('[getProjects] API_BASE:', API_BASE);
  
  if (USE_MOCK) {
    console.log('[getProjects] Using mock database');
    const mockProjects = await mockDB.getProjects(userId);
    console.log('[getProjects] Mock projects returned:', mockProjects.length);
    return mockProjects;
  }
  
  try {
    console.log('[getProjects] Fetching from API:', `/projects?userId=${userId}`);
    const result = await fetchApi(`/projects?userId=${userId}`);
    console.log('[getProjects] API response received:', result);
    
    if (!Array.isArray(result)) {
      console.warn('[getProjects] API response is not an array, returning empty array');
      return [];
    }
    
    console.log('[getProjects] Number of projects returned:', result.length);
    
    const mappedProjects = result.map((project: any) => ({
      ...project,
      id: project?.id != null ? project.id.toString() : project?.id,
      userId: project?.userId != null ? project.userId.toString() : project?.userId,
      tasks: Array.isArray(project?.tasks)
        ? project.tasks.map((task: any) => ({
            ...task,
            id: task?.id != null ? task.id.toString() : task?.id
          }))
        : []
    }));
    
    console.log('[getProjects] Mapped projects:', mappedProjects);
    return mappedProjects;
  } catch (error) {
    console.error('[getProjects] Error fetching projects:', error);
    throw error;
  }
};
export const createProject=async (inputs: any,tasks: any[])=> {
  if (USE_MOCK) {
    const projectId = 'proj-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    return mockDB.createProject({
      id: projectId,
      ...inputs,
      tasks,
      userId: inputs.userId,
      createdAt: new Date().toISOString()
    });
  }
  return await fetchApi('/projects',{method: 'POST',body: JSON.stringify({...inputs,tasks})});
};
export const deleteProject=async (projectId: string,userId?: string)=> {
  if (USE_MOCK) return mockDB.deleteProject(projectId);
  const query = userId ? `?userId=${userId}` : '';
  return await fetchApi(`/projects/${projectId}${query}`,{method: 'DELETE'});
};

export const renameProject = async (projectId: string, newName: string, userId?: string) => {
  if (USE_MOCK) return { success: true, projectId, newName };
  const query = userId ? `?userId=${userId}` : '';
  return await fetchApi(`/projects/${projectId}/rename${query}`, {
    method: 'PUT',
    body: JSON.stringify({ newName })
  });
};

export const updateProjectTasks=async (pid: string,tasks: any[])=> {
  console.log('[apiService] updateProjectTasks called for project:', pid, 'with', tasks.length, 'tasks');
  if (USE_MOCK) {
    console.log('[apiService] Using mock database');
    return mockDB.updateProjectTasks(pid,tasks);
  }
  const result = await fetchApi(`/projects/${pid}/tasks`,{
    method: 'PATCH',
    body: JSON.stringify({tasks})
  });
  console.log('[apiService] updateProjectTasks response:', result);
  return result;
};
export const generateProjectTasks = async (inputs: any) => {
  console.log('[API Service] Generating project tasks with inputs:', inputs);
  
  // Check AI feature access before proceeding
  const accessResult = await canUseAIFeatures();
  if (!accessResult.allowed) {
    console.warn('[API Service] AI feature access denied:', accessResult.reason);
    promptUpgradeIfNeeded(accessResult);
    throw new Error(accessResult.reason || 'AI features are not available in your current plan.');
  }
  
  let geminiError = null;
  let openRouterError = null;
  
  // Try Gemini first (primary AI provider)
  try {
    console.log('[API Service] Attempting Gemini AI generation...');
    const { generateProjectTasksGemini } = await import('./geminiService');
    const geminiResult = await generateProjectTasksGemini(inputs);
    
    // Check if Gemini returned actual tasks
    if (geminiResult && geminiResult.tasks && geminiResult.tasks.length > 0) {
      console.log('[API Service] ✓ Gemini AI succeeded:', geminiResult.projectName, 'with', geminiResult.tasks.length, 'tasks');
      // Log AI usage for tracking
      await logAIUsage('project_task_generation', inputs.projectName).catch(() => {});
      return geminiResult;
    }
    
    console.log('[API Service] Gemini returned empty result, trying OpenRouter fallback...');
    geminiError = 'Gemini returned no tasks';
  } catch (error) {
    geminiError = error instanceof Error ? error.message : 'Unknown error';
    console.warn('[API Service] Gemini AI failed:', geminiError);
    console.log('[API Service] Trying OpenRouter fallback...');
  }
  
  // Fallback to OpenRouter if Gemini fails or returns no tasks
  try {
    console.log('[API Service] Attempting OpenRouter AI generation...');
    const { generateProjectTasksOR } = await import('./openrouterService');
    const openRouterResult = await generateProjectTasksOR(inputs);
    
    if (openRouterResult && openRouterResult.tasks && openRouterResult.tasks.length > 0) {
      console.log('[API Service] ✓ OpenRouter AI succeeded:', openRouterResult.projectName, 'with', openRouterResult.tasks.length, 'tasks');
      // Log AI usage for tracking
      await logAIUsage('project_task_generation', inputs.projectName).catch(() => {});
      return openRouterResult;
    }
    
    console.log('[API Service] OpenRouter returned empty result');
    openRouterError = 'OpenRouter returned no tasks';
  } catch (error) {
    openRouterError = error instanceof Error ? error.message : 'Unknown error';
    console.warn('[API Service] OpenRouter AI also failed:', openRouterError);
  }
  
  // Final fallback: throw error with detailed information
  console.error('[API Service] ✗ All AI providers failed');
  console.error('[API Service] Gemini error:', geminiError);
  console.error('[API Service] OpenRouter error:', openRouterError);
  
  throw new Error(
    `AI generation failed\n\n` +
    `Gemini: ${geminiError || 'No error details'}\n` +
    `OpenRouter: ${openRouterError || 'No error details'}\n\n` +
    `Troubleshooting:\n` +
    `1. Check your API keys in Settings → Integrations\n` +
    `2. Verify API keys are valid and not expired\n` +
    `3. Check API provider dashboards for rate limits\n` +
    `4. Try again in a few moments`
  );
};

/**
 * Analyze a task using AI (Gemini first, then OpenRouter fallback)
 * @param task - The task object with title and description
 * @param context - Additional project context
 * @returns TaskAnalysis with improved description, suggested subtasks, and risks
 */
export const analyzeTask = async (task: any, context: string): Promise<TaskAnalysis> => {
  console.log('[analyzeTask] Starting task analysis...');
  console.log('[analyzeTask] Task:', task.title);
  console.log('[analyzeTask] Context:', context);

  // Check AI feature access before proceeding
  const accessResult = await canUseAITaskAnalysis();
  if (!accessResult.allowed) {
    console.warn('[analyzeTask] AI feature access denied:', accessResult.reason);
    promptUpgradeIfNeeded(accessResult);
    throw new Error(accessResult.reason || 'AI features are not available in your current plan.');
  }

  const taskTitle = task.title || '';
  const taskDescription = task.description || '';

  // Try Gemini first
  try {
    console.log('[analyzeTask] Attempting Gemini API...');
    const { analyzeTaskWithGemini } = await import('./geminiService');
    const geminiResult = await analyzeTaskWithGemini(taskTitle, taskDescription, context);
    
    if (geminiResult.success && geminiResult.subtasks && geminiResult.subtasks.length > 0) {
      console.log('[analyzeTask] ✓ Gemini analysis successful');
      // Log AI usage for tracking
      await logAIUsage('task_analysis', task.id).catch(() => {});
      return {
        improvedDescription: geminiResult.improvedDescription || taskDescription,
        suggestedSubtasks: geminiResult.subtasks,
        risks: geminiResult.risks || []
      };
    }
    
    console.warn('[analyzeTask] Gemini returned no data:', geminiResult.error);
  } catch (geminiError) {
    console.warn('[analyzeTask] Gemini failed:', geminiError);
  }

  // Fallback to OpenRouter
  try {
    console.log('[analyzeTask] Attempting OpenRouter API fallback...');
    const { analyzeTaskWithOpenRouter } = await import('./openrouterService');
    const openRouterResult = await analyzeTaskWithOpenRouter(taskTitle, taskDescription, context);
    
    if (openRouterResult.success && openRouterResult.subtasks && openRouterResult.subtasks.length > 0) {
      console.log('[analyzeTask] ✓ OpenRouter analysis successful');
      // Log AI usage for tracking
      await logAIUsage('task_analysis', task.id).catch(() => {});
      return {
        improvedDescription: openRouterResult.improvedDescription || taskDescription,
        suggestedSubtasks: openRouterResult.subtasks,
        risks: openRouterResult.risks || []
      };
    }
    
    console.warn('[analyzeTask] OpenRouter returned no data:', openRouterResult.error);
  } catch (openRouterError) {
    console.warn('[analyzeTask] OpenRouter failed:', openRouterError);
  }

  // If both fail, return default fallback
  console.error('[analyzeTask] Both AI services failed, returning fallback');
  throw new Error('AI analysis unavailable. Please configure Gemini or OpenRouter API keys in Settings → Integrations.');
};

/**
 * Project-level AI analytics: sentiment, critical path, risks, mitigations, actions
 */
export const analyzeProjectAnalytics = async (project: any): Promise<ProjectAIAnalytics> => {
  const accessResult = await canUseAITaskAnalysis();
  if (!accessResult.allowed) {
    promptUpgradeIfNeeded(accessResult);
    throw new Error(accessResult.reason || 'AI features are not available in your current plan.');
  }

  const projectName = project.name || 'Project';
  // Build a compact context string with tasks, subtasks, status, due dates, and risk flags
  const taskLines = (project.tasks || []).map((t: any) => {
    const subtasks = (t.subtasks || []).map((s: any) => `- ${s.title} [${s.status}]${s.isRisk ? ' (risk)' : ''}`).join('\n');
    const historySnippet = (t.history || []).slice(0, 3).map((h: any) => `${h.action} @ ${h.timestamp}`).join(' | ');
    return `Task: ${t.title} [${t.status}] Priority:${t.priority} Due:${t.dueDate || 'n/a'}\nDesc:${t.description || ''}\nSubtasks:\n${subtasks || 'None'}\nHistory:${historySnippet || 'n/a'}`;
  }).join('\n\n');

  const projectContext = `Project: ${projectName}\nIndustry: ${project.industry || ''}\nProcess: ${project.processType || ''}\nTasks:\n${taskLines}`;

  // Gemini first
  try {
    const { analyzeProjectAnalyticsWithGemini } = await import('./geminiService');
    const result = await analyzeProjectAnalyticsWithGemini(projectName, projectContext);
    if (result.success) {
      await logAIUsage('project_ai_analytics', project.id).catch(() => {});
      return {
        sentiment: result.sentiment || 'neutral',
        sentimentScore: result.sentimentScore,
        criticalPath: result.criticalPath || [],
        risks: result.risks || [],
        mitigations: result.mitigations || [],
        actions: result.actions || [],
        summary: result.summary || ''
      };
    }
    console.warn('[analyzeProjectAnalytics] Gemini returned no data:', result.error);
  } catch (err) {
    console.warn('[analyzeProjectAnalytics] Gemini failed:', err);
  }

  // OpenRouter fallback
  try {
    const { analyzeProjectAnalyticsWithOpenRouter } = await import('./openrouterService');
    const result = await analyzeProjectAnalyticsWithOpenRouter(projectName, projectContext);
    if (result.success) {
      await logAIUsage('project_ai_analytics', project.id).catch(() => {});
      return {
        sentiment: result.sentiment || 'neutral',
        sentimentScore: result.sentimentScore,
        criticalPath: result.criticalPath || [],
        risks: result.risks || [],
        mitigations: result.mitigations || [],
        actions: result.actions || [],
        summary: result.summary || ''
      };
    }
    console.warn('[analyzeProjectAnalytics] OpenRouter returned no data:', result.error);
  } catch (err) {
    console.warn('[analyzeProjectAnalytics] OpenRouter failed:', err);
  }

  throw new Error('AI analytics unavailable. Please configure Gemini or OpenRouter API keys in Settings → Integrations.');
};

export const generateSubtasks = async (
  description: string,
  completedSubtasks: string[] = [],
  pendingSubtasks: string[] = []
): Promise<Array<{title: string; description: string; priority: 'low' | 'medium' | 'high'; isRisk: boolean}> | string[]> => {
  // Check AI feature access before proceeding
  const accessResult = await canUseAISubtaskGeneration();
  if (!accessResult.allowed) {
    console.warn('[generateSubtasks] AI feature access denied:', accessResult.reason);
    promptUpgradeIfNeeded(accessResult);
    throw new Error(accessResult.reason || 'AI features are not available in your current plan.');
  }

  // Try Gemini first
  const { generateSmartSubtasksWithGemini } = await import('./geminiService');
  const geminiResult = await generateSmartSubtasksWithGemini(description, completedSubtasks, pendingSubtasks);
  
  if (geminiResult.success && geminiResult.subtasks && geminiResult.subtasks.length > 0) {
    // Log AI usage for tracking
    await logAIUsage('subtask_generation').catch(() => {});
    return geminiResult.subtasks;
  }
  
  console.warn('Gemini smart subtask generation failed, trying OpenRouter:', geminiResult.error);
  
  // Fallback to OpenRouter
  const { generateSmartSubtasksWithOpenRouter } = await import('./openrouterService');
  const openrouterResult = await generateSmartSubtasksWithOpenRouter(description, completedSubtasks, pendingSubtasks);
  
  if (openrouterResult.success && openrouterResult.subtasks && openrouterResult.subtasks.length > 0) {
    // Log AI usage for tracking
    await logAIUsage('subtask_generation').catch(() => {});
    return openrouterResult.subtasks;
  }
  
  console.error('Both Gemini and OpenRouter smart subtask generation failed');
  
  // Create detailed error message for the user
  let errorMessage = 'AI subtask generation failed. ';
  if (geminiResult.error && openrouterResult.error) {
    errorMessage += `Both services encountered issues. ${geminiResult.error}`;
  } else if (openrouterResult.error) {
    errorMessage += openrouterResult.error;
  } else {
    errorMessage += 'Please configure AI integration in Settings.';
  }
  
  throw new Error(errorMessage);
};
export const updateUserPlan=(plan: PlanTier)=> ({} as User);

export const createCheckoutSession=async (plan: PlanTier,interval: 'month' | 'year', couponCode?: string)=> {
  if (USE_MOCK) return mockDB.createCheckoutSession(plan,interval, couponCode);
  return await fetchApi('/billing/checkout',{method: 'POST',body: JSON.stringify({plan,interval, couponCode})});
};

export const verifyPendingChange=async (token: string)=> {if (USE_MOCK) return mockDB.verifyPendingChange(token);return await fetchApi(`/billing/verify/${token}`);};
export const completePlanChange=async (token: string,success: boolean)=> {if (USE_MOCK) return mockDB.completePlanChange(token,success);return await fetchApi(`/billing/complete`,{method: 'POST',body: JSON.stringify({token,success})});};
export const getInvoices=async ()=> {if (USE_MOCK) return mockDB.getInvoices();return await fetchApi('/billing/invoices');};
export const getAdminInvoices=async ()=> {if (USE_MOCK) return mockDB.getInvoices();return await fetchApi('/admin/invoices');};
export const getUserBillingInfo=async ()=> {if (USE_MOCK) return null;return await fetchApi('/billing/info');};
export const updateUserBillingInfo=async (data: any)=> {if (USE_MOCK) return null;return await fetchApi('/billing/info',{method: 'POST',body: JSON.stringify(data)});};
export const canRemovePaymentMethod=async ()=> {if (USE_MOCK) return {canRemove:true,hasPendingInvoices:false,hasCoupon:false};return await fetchApi('/billing/payment-method/can-remove');};
export const removePaymentMethod=async ()=> {if (USE_MOCK) return {success:true};return await fetchApi('/billing/payment-method',{method: 'DELETE'});};
export const getSupportTickets=async ()=> {if (USE_MOCK) return mockDB.getSupportTickets();return await fetchApi('/support/tickets');};
export const createSupportTicket=async (data: any)=> {if (USE_MOCK) return mockDB.createSupportTicket(data);await fetchApi('/support/tickets',{method: 'POST',body: JSON.stringify(data)});};
export const replyToSupportTicket=async (id: string,msg: string)=> {if (USE_MOCK) return mockDB.replyToSupportTicket(id,msg);await fetchApi(`/support/tickets/${id}/reply`,{method: 'POST',body: JSON.stringify({message: msg})});};
export const updateTicketStatus=async (id: string,status: any)=> {if (USE_MOCK) return mockDB.updateTicketStatus(id,status);await fetchApi(`/support/tickets/${id}/status`,{method: 'PUT',body: JSON.stringify({status})});};
export const getCRMLeads=async ()=> {if (USE_MOCK) return mockDB.getCRMLeads();return await fetchApi('/crm/leads');};
export const createCRMLead=async (lead: any)=> {if (USE_MOCK) return mockDB.createCRMLead(lead);await fetchApi('/crm/leads',{method: 'POST',body: JSON.stringify(lead)});};
export const getCRMLeadDetails=async (id: number)=> {if (USE_MOCK) return mockDB.getCRMLeadDetails(id);return await fetchApi(`/crm/leads/${id}`);};
export const addCRMComment=async (id: number,content: string)=> {if (USE_MOCK) return mockDB.addCRMComment(id,content);await fetchApi(`/crm/leads/${id}/comments`,{method: 'POST',body: JSON.stringify({content})});};
export const linkReminderToLead=async (leadId: number,title: string,date: string)=> {if (USE_MOCK) return mockDB.linkReminderToLead(leadId,title,date);await fetchApi(`/crm/leads/${leadId}/reminders`,{method: 'POST',body: JSON.stringify({title,date})});};
export const getReminders=async ()=> {if (USE_MOCK) return mockDB.getReminders();return await fetchApi('/reminders');};
export const createReminder=async (r: any)=> {if (USE_MOCK) return mockDB.createReminder(r);await fetchApi('/reminders',{method: 'POST',body: JSON.stringify(r)});};
export const updateReminder=async (id: number,r: any)=> {if (USE_MOCK) return mockDB.updateReminder(id,r);await fetchApi(`/reminders/${id}`,{method: 'PUT',body: JSON.stringify(r)});};
export const getFAQs=async ()=> {if (USE_MOCK) return mockDB.getFAQs();return await fetchApi('/cms/faqs');};
export const createFAQ=async (f: any)=> {if (USE_MOCK) return mockDB.createFAQ(f);await fetchApi('/cms/faqs',{method: 'POST',body: JSON.stringify(f)});};
export const updateFAQ=async (id: number,f: any)=> {if (USE_MOCK) return mockDB.updateFAQ(id,f);await fetchApi(`/cms/faqs/${id}`,{method: 'PUT',body: JSON.stringify(f)});};
export const deleteFAQ=async (id: number)=> {if (USE_MOCK) return mockDB.deleteFAQ(id);await fetchApi(`/cms/faqs/${id}`,{method: 'DELETE'});};
export const getCMSPage=async (slug: string)=> {if (USE_MOCK) return mockDB.getCMSPage(slug);return await fetchApi(`/cms/pages/${slug}`);};
export const updateCMSPage=async (slug: string,content: string)=> {if (USE_MOCK) return mockDB.updateCMSPage(slug,content);await fetchApi(`/cms/pages/${slug}`,{method: 'PUT',body: JSON.stringify({content})});};
export const generateNotifications=(p: any[])=> [];

// Admin Analytics
export const getAdminAnalytics = async () => {
  if (USE_MOCK) return {
    users: { total: 45, newThisWeek: 12, active: 42, byPlan: { kanban: 30, pro: 10, enterprise: 5 } },
    projects: { total: 1204, newThisMonth: 87 },
    tasks: { total: 4523, completed: 3102, inProgress: 856 },
    revenue: { mrr: 1450, growth: 5.2 },
    subscriptions: [{ plan_tier: 'Kanban', user_count: 30 }, { plan_tier: 'Pro', user_count: 10 }, { plan_tier: 'Enterprise', user_count: 5 }],
    signupTrend: [],
    system: { health: 'operational', errors24h: 0, warnings24h: 2 },
    support: { total: 23, open: 5, resolved: 18 }
  };
  return await fetchApi('/admin/analytics');
};

export const saveProjectAIAnalyticsHistory = async (
  projectId: string,
  category: string,
  payload: {
    contentHtml: string;
    sentiment?: string;
    sentimentScore?: number;
    criticalPath?: string[];
    risks?: string[];
    mitigations?: string[];
    actions?: string[];
    summary?: string;
  }
) => {
  return fetchApi('/user/ai-analytics/history', {
    method: 'POST',
    body: JSON.stringify({ projectId, category, ...payload })
  });
};

export const getProjectAIAnalyticsHistory = async (
  projectId: string,
  from?: string,
  to?: string
): Promise<any[]> => {
  const params = new URLSearchParams({ projectId });
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return fetchApi(`/user/ai-analytics/history?${params.toString()}`);
};

export const searchUsers = async (query: string) => {
  if (USE_MOCK) {
    console.log('[API] Using mock search');
    return mockDB.searchUsers?.(query) || [];
  }
  console.log('[API] Searching for:', query);
  const result = await fetchApi(`/users/search?q=${encodeURIComponent(query)}`);
  console.log('[API] Search result:', result);
  return result;
};

export const inviteUserToProject = async (
  email: string,
  projectId: string,
  taskId?: string
) => {
  if (USE_MOCK) {
    return { success: true, email, projectId };
  }
  return await fetchApi('/projects/invite-user', {
    method: 'POST',
    body: JSON.stringify({ email, projectId, taskId })
  });
};

export const searchProjectUsers = async (projectId: string, query?: string) => {
  if (USE_MOCK) {
    return [];
  }
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  
  return await fetchApi(`/projects/${projectId}/users?${params.toString()}`);
};

// =============================================
// DATABASE BACKUP & RESTORE
// =============================================

export const backupFullDatabase = async (): Promise<Blob> => {
  if (USE_MOCK) {
    throw new Error('Database backup not available in mock mode');
  }

  const csrfToken = getCookie('csrf_token');
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_BASE}/admin/database/backup`, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  if (!response.ok) {
    // Check if response is JSON (error) or blob (should not happen for errors)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || `API Error: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      throw error;
    }
    throw new Error(`Backup failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
};

export const backupUserData = async (email: string): Promise<Blob> => {
  if (USE_MOCK) {
    throw new Error('Database backup not available in mock mode');
  }

  const csrfToken = getCookie('csrf_token');
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_BASE}/admin/database/backup/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || `API Error: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      throw error;
    }
    throw new Error(`Backup failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
};

export const backupUserDataExcel = async (email: string): Promise<Blob> => {
  if (USE_MOCK) {
    throw new Error('Database backup not available in mock mode');
  }

  const csrfToken = getCookie('csrf_token');
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_BASE}/admin/database/backup/excel/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || `API Error: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      throw error;
    }
    throw new Error(`Backup failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
};

export const restoreDatabase = async (sqlFile: File): Promise<void> => {
  if (USE_MOCK) {
    throw new Error('Database restore not available in mock mode');
  }
  
  const csrfToken = getCookie('csrf_token');
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  
  const formData = new FormData();
  formData.append('sqlFile', sqlFile);
  
  const response = await fetch(`${API_BASE}/admin/database/restore`, {
    method: 'POST',
    headers, // Don't set Content-Type - let browser handle multipart/form-data
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || `API Error: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      throw error;
    }
    throw new Error(`Restore failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Resend invite with coupon code
export const resendInvite = async (inviteId: number, couponCode?: string) => {
  if (USE_MOCK) {
    const invite = mockDB.pendingInvites?.find(i => i.id === inviteId);
    if (invite) {
      invite.status = 'pending';
      invite.coupon_code = couponCode;
    }
    return { success: true, message: 'Invite resent successfully' };
  }

  return fetchApi(`/api/pending-invites/${inviteId}/resend`, {
    method: 'POST',
    body: JSON.stringify({ couponCode })
  });
};

// Get invite statistics for user
export const getInviteStats = async () => {
  if (USE_MOCK) {
    return {
      total: mockDB.pendingInvites?.length || 0,
      pending: mockDB.pendingInvites?.filter(i => i.status === 'pending').length || 0,
      accepted: mockDB.pendingInvites?.filter(i => i.status === 'accepted').length || 0,
      rejected: mockDB.pendingInvites?.filter(i => i.status === 'rejected').length || 0,
      expired: mockDB.pendingInvites?.filter(i => i.status === 'expired').length || 0,
    };
  }

  return fetchApi('/api/pending-invites/stats/summary');
};